/**
 * Conversions between the canonical metric units and their imperial
 * counterparts.
 *
 * The API transports metric and only metric: kilograms, centimetres,
 * millilitres. Imperial values exist for the length of a render, and this
 * module is the only place that knows how to produce them. The mobile app
 * (`lib/utils/units`) and the backend (`internal/units`) pin the same numbers.
 *
 * The factors are exact legal definitions, not tunables: the 1959 international
 * yard and pound agreement fixes one pound at 0.45359237 kg and one inch at
 * 2.54 cm. They are compiled in rather than fetched, so a conversion can never
 * depend on configuration that is missing, stale, or out of step between two
 * machines.
 *
 * Nothing here rounds. Rounding belongs to display alone — a value rounded on
 * its way into storage cannot be recovered.
 */
export const POUND_TO_KILOGRAM = 0.45359237;
export const INCH_TO_CENTIMETRE = 2.54;
export const US_FLUID_OUNCE_TO_MILLILITRE = 29.5735295625;
/**
 * The avoirdupois ounce — the one food is weighed in. Not the fluid ounce
 * above, which measures volume.
 */
export const GRAM_TO_OUNCE = 28.349523125;
export const INCHES_PER_FOOT = 12;
export function kilogramsToPounds(kg) {
    return kg / POUND_TO_KILOGRAM;
}
export function poundsToKilograms(pounds) {
    return pounds * POUND_TO_KILOGRAM;
}
export function centimetresToInches(cm) {
    return cm / INCH_TO_CENTIMETRE;
}
export function inchesToCentimetres(inches) {
    return inches * INCH_TO_CENTIMETRE;
}
export function millilitresToFluidOunces(ml) {
    return ml / US_FLUID_OUNCE_TO_MILLILITRE;
}
export function gramsToOunces(grams) {
    return grams / GRAM_TO_OUNCE;
}
export function ouncesToGrams(ounces) {
    return ounces * GRAM_TO_OUNCE;
}
/** Unit for a food mass — a portion, a macronutrient, a serving. */
export function portionSuffix(system) {
    return system === "metric" ? "g" : "oz";
}
/**
 * A food mass rendered for reading.
 *
 * Ounces carry a decimal where grams do not: a macro shown as one whole ounce
 * would cover everything from 21 to 42 grams.
 */
export function formatPortion(grams, system) {
    if (system === "metric") {
        return `${Math.round(grams)} g`;
    }
    return `${gramsToOunces(grams).toFixed(1)} oz`;
}
/**
 * Splits a height into whole feet plus remaining whole inches.
 *
 * Total inches are rounded once, before the split, so a height just short of
 * the next foot reads as 6'0" rather than 5'12".
 */
export function centimetresToFeetAndInches(cm) {
    const totalInches = Math.round(centimetresToInches(cm));
    return {
        feet: Math.floor(totalInches / INCHES_PER_FOOT),
        inches: totalInches % INCHES_PER_FOOT,
    };
}
/**
 * Reads a stored mass as kilograms.
 *
 * The backend normalises on write, so a non-metric unit here means a value
 * predating that. Converting is safe; guessing is not, which is why an
 * unrecognised unit yields null rather than being read as kilograms — a weight
 * silently reinterpreted is wrong by more than a factor of two.
 */
export function massToKilograms(value, unit) {
    if (!Number.isFinite(value))
        return null;
    switch (normaliseSymbol(unit)) {
        case "":
        case "kg":
        case "kilogram":
        case "kilograms":
            return value;
        case "lb":
        case "lbs":
        case "pound":
        case "pounds":
            return poundsToKilograms(value);
        default:
            return null;
    }
}
/** Reads a stored length as centimetres. */
export function lengthToCentimetres(value, unit) {
    if (!Number.isFinite(value))
        return null;
    switch (normaliseSymbol(unit)) {
        case "":
        case "cm":
        case "centimetre":
        case "centimetres":
        case "centimeter":
        case "centimeters":
            return value;
        case "m":
            return value * 100;
        case "in":
        case "inch":
        case "inches":
            return value * INCH_TO_CENTIMETRE;
        default:
            return null;
    }
}
/** Decimals shown for a body weight, in either system. */
export const WEIGHT_FRACTION_DIGITS = 1;
export function weightSuffix(system) {
    return system === "metric" ? "kg" : "lb";
}
export function heightSuffix(system) {
    return system === "metric" ? "cm" : "ft/in";
}
/** A weight rendered as a bare number in the reader's system. */
export function formatWeightValue(kg, system) {
    const shown = system === "metric" ? kg : kilogramsToPounds(kg);
    return shown.toFixed(WEIGHT_FRACTION_DIGITS);
}
/** A weight with its unit, for read-only display. */
export function formatWeight(kg, system) {
    return `${formatWeightValue(kg, system)} ${weightSuffix(system)}`;
}
/** A height with its unit, for read-only display. */
export function formatHeight(cm, system) {
    if (system === "metric") {
        return `${Math.round(cm)} cm`;
    }
    const { feet, inches } = centimetresToFeetAndInches(cm);
    return `${feet}'${inches}"`;
}
function normaliseSymbol(unit) {
    return (unit !== null && unit !== void 0 ? unit : "").trim().toLowerCase();
}
